import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Send, X } from "lucide-react";
import { BACKEND, api, mediaUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useBi } from "../lib/customer";
import { fmtSAR } from "../lib/format";

const SID = "lf_chat_sid";

function Cards({ cards = [], projects = [], onNav }) {
  const { tr, lang } = useI18n();
  if (!cards.length && !projects.length) return null;
  return (
    <div className="mt-2 grid gap-2">
      {cards.map((c) => (
        <Link key={c.id} to={`/properties/${c.slug}`} onClick={onNav} data-testid={`chat-card-${c.slug}`} className="flex gap-3 rounded-sm border border-cta/25 bg-canvas/70 p-2 transition-colors hover:border-cta">
          {c.images?.[0] && <img src={mediaUrl(c.images[0])} alt="" className="h-14 w-20 shrink-0 rounded-sm object-cover" />}
          <div className="min-w-0 text-xs">
            <p className="truncate font-medium text-fg">{tr(c, "title")}</p>
            <p className="text-cta">{fmtSAR(c.price, lang)}</p>
            {c.is_demo && <span className="text-[10px] uppercase tracking-wider text-flame">Demo</span>}
          </div>
        </Link>
      ))}
      {projects.map((p) => (
        <Link key={p.id} to={`/developments/${p.slug}`} onClick={onNav} data-testid={`chat-project-${p.slug}`} className="rounded-sm border border-fg/15 bg-canvas/70 p-2 text-xs text-fg hover:border-cta">{tr(p, "title")}</Link>
      ))}
    </div>
  );
}

export default function ChatWidget() {
  const b = useBi();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("auto");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);
  useEffect(() => {
    const sid = localStorage.getItem(SID);
    if (open && sid && !msgs.length) api.get(`/ai/chat/${sid}`).then((r) => setMsgs(r.data.messages)).catch(() => localStorage.removeItem(SID));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (e) => {
    e.preventDefault();
    const m = text.trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m }, { role: "assistant", content: "", streaming: true }]);
    const patchLast = (p) => setMsgs((x) => x.map((y, i) => (i === x.length - 1 ? { ...y, ...(typeof p === "function" ? p(y) : p) } : y)));
    try {
      const res = await fetch(`${BACKEND}/api/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: m, session_id: localStorage.getItem(SID), lang: pick === "auto" ? lang : pick, page: window.location.pathname }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Error");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.type === "session") localStorage.setItem(SID, ev.session_id);
          if (ev.type === "delta") patchLast((y) => ({ content: y.content + ev.text }));
          if (ev.type === "done") patchLast({ content: ev.reply, cards: ev.cards, projects: ev.projects, disclaimer: ev.disclaimer, lead_created: ev.lead_created, streaming: false });
        }
      }
    } catch (err) {
      patchLast({ content: b(`Sorry — ${err.message}. Please try again or contact our team.`, `عذراً — ${err.message}. حاول مجدداً أو تواصل مع فريقنا.`), streaming: false });
    } finally { setBusy(false); }
  };
  const reset = () => { localStorage.removeItem(SID); setMsgs([]); };
  const disc = b("AI-generated guidance — please confirm with our team.", "إرشادات مولّدة بالذكاء الاصطناعي — يرجى التأكد مع فريقنا.");

  return (
    <>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="chat-widget-toggle" aria-label={b("Open property assistant", "فتح المساعد العقاري")}
        className="fixed bottom-6 end-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-cta text-canvas shadow-xl ring-1 ring-fg/30 transition-transform duration-300 hover:scale-110">
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>
      {open && (
        <div role="dialog" aria-label={b("Property assistant", "المساعد العقاري")} data-testid="chat-widget-panel" className="glass fixed bottom-24 end-4 z-50 flex h-[min(620px,calc(100vh-8rem))] w-[min(400px,calc(100vw-2rem))] flex-col overflow-hidden rounded-sm border border-cta/30 bg-canvas/95 shadow-2xl">
          <div className="flex items-center justify-between gap-2 border-b border-fg/10 bg-brand/60 px-4 py-3">
            <div><p className="font-display text-lg text-fg">{b("Property Assistant", "المساعد العقاري")}</p><p className="text-[10px] uppercase tracking-widest text-cta">AI · Landmark Future</p></div>
            <div className="flex items-center gap-1">
              <select aria-label="Language" value={pick} onChange={(e) => setPick(e.target.value)} data-testid="chat-lang-select" className="rounded-sm border border-fg/20 bg-canvas px-1.5 py-1 text-xs text-fg">
                <option value="auto">{b("Auto", "تلقائي")}</option><option value="en">English</option><option value="ar">العربية</option>
              </select>
              <button type="button" onClick={reset} data-testid="chat-new-session" className="rounded-sm px-2 py-1 text-[11px] text-fg/70 hover:text-cta">{b("New chat", "محادثة جديدة")}</button>
            </div>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages" aria-live="polite">
            {!msgs.length && (
              <div className="text-sm text-fg/70">
                <p>{b("Hello! Ask me about our properties and developments — e.g. “villa in Riyadh under SAR 5 million”.", "مرحباً! اسألني عن عقاراتنا ومشاريعنا — مثلاً «فيلا في الرياض بأقل من ٥ ملايين ريال».")}</p>
                <p className="mt-2 text-[11px] text-fg/45">{disc}</p>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`chat-msg-${m.role}-${i}`}>
                <div className={`max-w-[88%] whitespace-pre-wrap rounded-sm px-3 py-2 text-sm ${m.role === "user" ? "bg-cta/90 text-canvas" : "bg-fg/[0.06] text-fg/90"}`} dir="auto">
                  {m.content || (m.streaming ? <span className="animate-pulse">…</span> : "")}
                  {m.role === "assistant" && <Cards cards={m.cards} projects={m.projects} onNav={() => setOpen(false)} />}
                  {m.lead_created && <p data-testid="chat-lead-created" className="mt-2 text-xs text-cta">{b("✓ Your details were sent to our team.", "✓ تم إرسال بياناتك إلى فريقنا.")}</p>}
                  {m.role === "assistant" && !m.streaming && m.content && <p className="mt-2 border-t border-fg/10 pt-1.5 text-[10px] text-fg/45" data-testid="chat-disclaimer">{m.disclaimer || disc}</p>}
                </div>
              </div>
            ))}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="flex gap-2 border-t border-fg/10 p-3">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} dir="auto" placeholder={b("Type your message…", "اكتب رسالتك…")} aria-label={b("Message", "الرسالة")} data-testid="chat-input" className="field !py-2.5" />
            <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send" aria-label={b("Send", "إرسال")} className="btn-cta !px-4 !py-2.5 disabled:opacity-40"><Send className="rtl-flip h-4 w-4" /></button>
          </form>
          <p className="px-3 pb-2 text-[10px] text-fg/40">{b("By sharing details you can agree to our", "عند مشاركة بياناتك يمكنك الموافقة على")} <Link to="/privacy" className="text-cta" onClick={() => setOpen(false)}>{b("Privacy Policy", "سياسة الخصوصية")}</Link></p>
        </div>
      )}
    </>
  );
}
